import { z } from "zod";
import { timestampSchema, uuidSchema } from "../common/primitives.js";
/**
 * Mobil biyometrik step-up — cihaz anahtarı (Prompt 21, ADR 0021 §4). Uygulama bir ed25519 anahtar çifti üretir; özel anahtar
 * cihazın güvenli deposunda biyometriyle korunur, açık anahtar taze step-up ile kaydedilir. Step-up anında tek kullanımlık
 * meydan okuma imzalanır → sunucu `auth_time`'ı tazeler (parola / TOTP step-up'ıyla AYNI sonuç).
 */
export const AUTH_DEVICE_PLATFORMS = ["IOS", "ANDROID"];
export const authDevicePlatformSchema = z.enum(AUTH_DEVICE_PLATFORMS);
/** ed25519 açık anahtarı: ham 32 bayt, standart base64 (ajan anahtarıyla aynı biçim). */
export const authDevicePublicKeySchema = z
    .string()
    .regex(/^[A-Za-z0-9+/]{43}=$/, "Açık anahtar base64 kodlu 32 bayt olmalıdır")
    .describe("ed25519 açık anahtarı (base64, 32 bayt)");
/** ed25519 imzası: 64 bayt, standart base64. */
export const authDeviceSignatureSchema = z
    .string()
    .regex(/^[A-Za-z0-9+/]{86}==$/, "İmza base64 kodlu 64 bayt olmalıdır")
    .describe("ed25519 imzası (base64, 64 bayt)");
/** Meydan okuma: 32 rastgele bayt, base64url (dolgusuz). */
export const authDeviceNonceSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
/** Meydan okumanın geçerlilik süresi (sn). */
export const AUTH_DEVICE_CHALLENGE_TTL_SECONDS = 120;
export const AUTH_DEVICE_REVOKE_REASONS = [
    "USER",
    "PASSWORD_CHANGED",
    "ALL_SESSIONS_REVOKED",
    "ADMIN",
    "REPLACED",
];
export const registerAuthDeviceSchema = z.object({
    publicKey: authDevicePublicKeySchema,
    platform: authDevicePlatformSchema,
    deviceName: z.string().trim().min(1).max(100).nullable().default(null),
    appVersion: z.string().trim().min(1).max(40).nullable().default(null),
    /** Aynı cihazın ÖNCEKİ anahtarı (uygulama yeniden kurulumu / anahtar yenileme) — verilirse iptal edilir (`REPLACED`). */
    replacesDeviceId: uuidSchema.nullable().default(null),
});
export const authDeviceSchema = z.object({
    id: uuidSchema,
    platform: authDevicePlatformSchema,
    deviceName: z.string().nullable(),
    appVersion: z.string().nullable(),
    createdAt: timestampSchema,
    lastUsedAt: timestampSchema.nullable(),
});
export const authDeviceListSchema = z.object({ items: z.array(authDeviceSchema) });
export const authDeviceParamsSchema = z.object({ id: uuidSchema });
export const authDeviceChallengeSchema = z.object({
    challengeId: uuidSchema,
    nonce: authDeviceNonceSchema,
    expiresAt: timestampSchema,
});
export const deviceReauthenticateSchema = z.object({
    deviceId: uuidSchema,
    challengeId: uuidSchema,
    signature: authDeviceSignatureSchema,
});
export const MOBILE_STEP_UP_SIGNATURE_PREFIX = "station-mobile-step-up";
/**
 * İmzalanan bayt dizisi (UTF-8, satır sonu `\n`, SONDA `\n` YOK):
 *   "station-mobile-step-up\n" + deviceId + "\n" + challengeId + "\n" + nonce
 * Meydan okuma oturuma bağlıdır (sunucu başka oturumdan gelen imzayı kabul etmez); saf işlev — mobil de aynısını üretir.
 */
export function mobileStepUpSignatureMessage(input) {
    return `${MOBILE_STEP_UP_SIGNATURE_PREFIX}\n${input.deviceId}\n${input.challengeId}\n${input.nonce}`;
}
//# sourceMappingURL=devices.js.map